<template>
  <div class="timeline-container" ref="timelineContainer">
    <div class="timeline-line"></div>

    <div
      v-for="(session, index) in visibleSessions"
      :key="session.execution._id"
      class="timeline-item"
      :ref="
        (el) => {
          if (el) itemRefs[index] = el
        }
      "
      :style="getDynamicStyles(session)"
      @click="$emit('view-session', session, $event)">
      <div class="timeline-dot"></div>

      <div class="card-content">
        <template v-if="isHyroxSession(session)">
          <div class="hyrox-card-overlay">
            <div class="session-header text-center">
              <h2 class="hyrox-card-title">HYROX</h2>
              <div class="hyrox-category-display">
                <v-icon small class="mr-2">{{ getHyroxCategoryDetails(session).icon }}</v-icon>
                <span>{{ getHyroxCategoryDetails(session).text }}</span>
              </div>
            </div>
            <div class="session-footer">
              <div class="total-time">
                <span class="stat-value">{{ formatDuration(session.execution.duration) }}</span>
                <span class="stat-label">Gesamtzeit</span>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="background-image" :style="{ backgroundImage: `url(${getImageUrl(session)})` }"></div>
          <div class="content-overlay">
            <div class="session-header text-center">
              <h2 class="session-title">{{ session.execution.title }}</h2>
              <p class="session-date">{{ formatDate(session.execution) }}</p>
            </div>
            <div class="session-footer">
              <div class="session-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ formatDuration(session.execution.duration) }}</span>
                  <span class="stat-label">Dauer</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ Math.round(session.execution.liftedWeight) }} <span class="unit">kg</span></span>
                  <span class="stat-label">Volumen</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ session.execution.caloriesBurned }} <span class="unit">kcal</span></span>
                  <span class="stat-label">Kalorien</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div ref="scrollTrigger" v-if="visibleCount < sessions.length" class="scroll-trigger"></div>
  </div>
</template>

<script>
import moment from "moment"

const INITIAL_LOAD_COUNT = 8
const LOAD_MORE_COUNT = 8

export default {
  name: "WorkoutTimeline",
  props: {
    sessions: {
      type: Array,
      required: true,
    },
    visualizationMap: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      itemRefs: [],
      animationObserver: null,
      scrollObserver: null, // NEU: Separater Observer für das Scrollen
      visibleCount: INITIAL_LOAD_COUNT, // NEU: Zählt die sichtbaren Elemente
    }
  },
  computed: {
    // NEU: Diese Property gibt nur die aktuell sichtbaren Sessions zurück
    visibleSessions() {
      return this.sessions.slice(0, this.visibleCount)
    },
  },
  watch: {
    // NEU: Beobachtet Änderungen an der sichtbaren Liste, um Animationen für neue Elemente zu aktivieren
    visibleSessions() {
      this.$nextTick(() => {
        this.observeItemsForAnimation()
      })
    },
  },
  methods: {
    isHyroxSession(session) {
      return session.execution && session.execution.hyroxSegments && session.execution.hyroxSegments.length > 0
    },
    getHyroxCategoryDetails(session) {
      if (!session.execution || !session.execution.title) {
        return { icon: "mdi-dumbbell", text: "Training" }
      }
      const title = session.execution.title

      if (title.includes("Men Pro")) return { icon: "mdi-crown", text: "Men Pro" }
      if (title.includes("Women Pro")) return { icon: "mdi-crown-outline", text: "Women Pro" }
      if (title.includes("Men")) return { icon: "mdi-human-male", text: "Men" }
      if (title.includes("Women")) return { icon: "mdi-human-female", text: "Women" }
      if (title.includes("Üben")) return { icon: "mdi-dumbbell", text: "Freies Training" }

      return { icon: null, text: title.replace("HYROX ", "") }
    },
    hexToRgb(hex) {
      let r = 0,
        g = 0,
        b = 0
      if (hex.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
        let c = hex.substring(1).split("")
        if (c.length === 3) {
          c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        }
        c = "0x" + c.join("")
        r = (c >> 16) & 255
        g = (c >> 8) & 255
        b = c & 255
      }
      return `${r}, ${g}, ${b}`
    },
    getDynamicStyles(session) {
      const viz = session.execution.fixedVisualization || this.visualizationMap[session.execution.title] || { color: "#888888" }
      return {
        "--dynamic-color": viz.color,
        "--dynamic-color-rgb": this.hexToRgb(viz.color),
      }
    },
    getImageUrl(session) {
      const viz = session.execution.fixedVisualization || this.visualizationMap[session.execution.title] || { img: "" }
      return viz.img
    },
    formatDate(execution) {
      // Wenn ts existiert, nutze es (ts ist in Sekunden, daher .unix())
      if (execution.ts) {
        return moment.unix(execution.ts).format("dd, DD. MMMM YYYY")
      }
      // Fallback, falls ts fehlt
      return moment(execution.createdAt).format("dd, DD. MMMM YYYY")
    },
    formatDuration(totalSeconds) {
      if (isNaN(totalSeconds) || totalSeconds < 0) return totalSeconds
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
    },
    // Umbenannt, um die Funktion klarer zu machen
    observeItemsForAnimation() {
      if (!this.animationObserver) {
        this.animationObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible")
                this.animationObserver.unobserve(entry.target) // Animation nur einmal auslösen
              }
            })
          },
          { threshold: 0.2 },
        )
      }
      // Nur die noch nicht beobachteten Elemente hinzufügen
      this.itemRefs.forEach((item) => {
        if (item) this.animationObserver.observe(item)
      })
    },
    // NEU: Methode zum Nachladen
    loadMore() {
      if (this.visibleCount < this.sessions.length) {
        this.visibleCount += LOAD_MORE_COUNT
      }
    },
    // NEU: Methode, um den Scroll-Trigger zu beobachten
    observeScrollTrigger() {
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.loadMore()
          }
        },
        { rootMargin: "200px" }, // Lädt etwas, bevor der User ganz unten ist
      )

      if (this.$refs.scrollTrigger) {
        this.scrollObserver.observe(this.$refs.scrollTrigger)
      }
    },
  },
  mounted() {
    this.observeItemsForAnimation()
    this.observeScrollTrigger()
  },
  beforeDestroy() {
    if (this.animationObserver) {
      this.animationObserver.disconnect()
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  },
}
</script>

<style scoped>
/* Das CSS bleibt exakt gleich wie in der letzten Version, */
/* hier sind keine Änderungen nötig. */
.timeline-container {
  height: 100%;
  overflow-y: scroll;
  padding: 20px 20px 20px 45px;
  position: relative;
}
.scroll-trigger {
  height: 50px; /* Gibt dem Trigger-Element eine kleine Höhe */
}
.timeline-line {
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}
.timeline-item {
  position: relative;
  margin-bottom: 24px;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.timeline-item.is-visible {
  opacity: 1;
  transform: translateY(0);
}
.timeline-dot {
  position: absolute;
  left: -33px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--dynamic-color);
  border: 4px solid #1e1e1e;
  box-shadow: 0 0 10px rgba(var(--dynamic-color-rgb), 0.7);
  z-index: 2;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}
.timeline-item:hover .timeline-dot {
  transform: translateY(-50%) scale(1.2);
}
.card-content {
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.25), 0 8px 20px -8px rgba(var(--dynamic-color-rgb), 0.6);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.card-content:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 12px 30px -10px rgba(var(--dynamic-color-rgb), 0.7);
}
.background-image {
  opacity: 0.2;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.6s ease;
}
.card-content:hover .background-image {
  transform: scale(1.05);
}
.content-overlay {
  position: relative;
  z-index: 1;
  color: white;
  padding: 16px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  background: linear-gradient(180deg, rgba(var(--dynamic-color-rgb), 0.2) 0%, transparent 40%, rgba(0, 0, 0, 0.85) 100%);
}
.session-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}
.session-date {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-bottom: 12px;
}
.session-stats {
  display: flex;
  justify-content: space-around;
  background: rgba(30, 30, 30, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 10px;
}
.stat-item {
  text-align: center;
}
.stat-value {
  font-size: 1.1rem;
  font-weight: 600;
  display: block;
}
.stat-label {
  font-size: 0.6rem;
  opacity: 0.7;
  text-transform: uppercase;
}
.unit {
  font-size: 0.7rem;
  font-weight: 400;
  opacity: 0.7;
}

.hyrox-card-overlay {
  position: relative;
  z-index: 1;
  color: white;
  padding: 16px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.hyrox-card-title {
  font-size: 3rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: white;

  text-shadow: 0 0 15px rgba(255, 237, 0, 0.5);
  line-height: 1;
}

.total-time {
  text-align: center;
  background: rgba(30, 30, 30, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 10px;
}
.hyrox-card-overlay {
  position: relative;
  z-index: 1;
  color: white;
  padding: 16px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  background: linear-gradient(180deg, rgba(255, 237, 0, 0.15) 0%, transparent 40%, rgba(0, 0, 0, 0.95) 100%), url("/img/noise-texture.png"); /* Subtile Textur für mehr "Grit" */
  background-blend-mode: overlay;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.hyrox-category-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 0.9rem;
  font-weight: bold;
  color: #cccccc;
}
.hyrox-category-display .v-icon {
  color: #ffed00;
}
</style>
